import { useState } from 'react';
import { getPref, setPref, signOutSafely, type ThemePref } from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  Field,
  Select,
  TimeInState,
  applyTheme,
  syncStateLabel,
  syncStateTone,
  useToast,
} from '@ecobox/ui';
import { APP_VERSION, ECOBOX_ENV } from '../env';
import { useBranch, useLedger, useServices, useSession, useSyncState } from '../services';
import { useOptionalDriverData } from '../hooks/useDriver';
import { useSessionEngine } from '../sessions';
import { fmt } from './driver/shared';

export function MePage() {
  const services = useServices();
  const { writer, client, ledger: deviceLedger, connectivity } = services;
  const session = useSession();
  const branch = useBranch();
  const sync = useSyncState();
  const ledger = useLedger();
  const toast = useToast();
  const engine = useSessionEngine();
  const driver = useOptionalDriverData();
  const [theme, setTheme] = useState<ThemePref>(getPref('theme'));
  const [blocked, setBlocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signOut = async () => {
    setBusy(true);
    setBlocked(null);
    try {
      // Scans still waiting on this phone become commands first, so sign-out can check them.
      if (session.uid) engine.flushAll(session.uid);
      const result = await signOutSafely({
        auth: client.auth,
        db: client.db,
        ledger: deviceLedger,
        connectivity,
      });
      if (!result.ok) setBlocked(result.message);
      else if (session.uid) engine.clearUser(session.uid);
    } finally {
      setBusy(false);
    }
  };

  const recent = [...ledger].reverse().slice(0, 30);

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Me</h1>

      <Card title={session.profile?.displayName ?? 'Profile'} data-tour="me-profile">
        <div className="ops-stack">
          <div className="ops-muted">
            {session.profile?.username ?? ''} · {session.claims?.roleIds.join(', ') || 'no roles'}
          </div>
          <Field label="Branch">
            {(p) => (
              <Select
                {...p}
                size="touch"
                value={branch.current ?? ''}
                onChange={(e) => services.branch.select(e.target.value)}
              >
                {branch.options.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Theme">
            {(p) => (
              <Select
                {...p}
                size="touch"
                value={theme}
                onChange={(e) => {
                  const next = e.target.value as ThemePref;
                  setTheme(next);
                  setPref('theme', next);
                  applyTheme(next);
                }}
              >
                <option value="system">Follow the phone</option>
                <option value="light">Light (sunlight)</option>
                <option value="dark">Dark</option>
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {driver?.isDriver ? (
        <Card title="Cash in my custody" data-tour="me-cash">
          {driver.custody.length === 0 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              None. Cash you take at the door shows here until you hand it to the cashier.
            </p>
          ) : (
            <ul className="ops-list" aria-label="Cash in custody">
              {driver.custody.map((c) => (
                <li key={c.id} className="ops-row">
                  <div className="ops-row__top">
                    <span>{c.branchId}</span>
                    <strong>{fmt(c.heldMinor, c.currency)}</strong>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      <Card title="Sync details" data-tour="me-sync">
        <div className="ops-stack">
          <div className="ops-muted">
            {sync.online ? 'Online' : 'Offline'} · {sync.pending} waiting · {sync.notDelivered} not
            delivered · {sync.rejected} rejected
          </div>
          {recent.length === 0 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              Nothing has been sent from this phone yet.
            </p>
          ) : (
            <ul className="ops-list" aria-label="Commands">
              {recent.map((e) => (
                <li
                  key={e.id}
                  className="ops-row"
                  data-tone={
                    e.state === 'rejected' || e.state === 'not_delivered'
                      ? 'bad'
                      : e.state === 'pending' || e.state === 'synced'
                        ? 'waiting'
                        : 'ok'
                  }
                >
                  <div className="ops-row__top">
                    <strong>{e.type}</strong>
                    <Badge tone={syncStateTone(e.state)}>{syncStateLabel(e.state)}</Badge>
                  </div>
                  <div className="ops-muted">
                    {typeof e.payload.message === 'string' ? `${e.payload.message} · ` : ''}
                    <TimeInState since={e.createdAt} suffix="ago" />
                  </div>
                  {e.lastError ? (
                    <div style={{ color: 'var(--bad)' }}>{e.lastError.message}</div>
                  ) : null}
                  {e.state === 'not_delivered' ? (
                    <Button size="lg" variant="secondary" onClick={() => void writer.resend(e.id)}>
                      Resend
                    </Button>
                  ) : null}
                  {e.state === 'rejected' && e.dismissed !== true ? (
                    <Button size="lg" variant="ghost" onClick={() => void writer.dismiss(e.id)}>
                      Dismiss
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Card title="Sign out" data-tour="me-signout">
        <div className="ops-stack">
          {sync.unsent > 0 ? (
            <div
              role="alert"
              className="ops-muted"
              style={{ color: 'var(--warn)', fontWeight: 700 }}
            >
              {sync.unsent} command{sync.unsent === 1 ? ' is' : 's are'} still waiting to sync. You
              can sign out once they have reached the server.
            </div>
          ) : null}
          {blocked ? (
            <div role="alert" style={{ color: 'var(--bad)', fontWeight: 700 }}>
              {blocked}
            </div>
          ) : null}
          <Button
            size="touch"
            variant="danger"
            block
            loading={busy}
            onClick={() => {
              void signOut().catch((err: unknown) =>
                toast.show({ title: 'Sign out failed', message: String(err), tone: 'danger' }),
              );
            }}
          >
            Sign out
          </Button>
        </div>
      </Card>

      <p className="ops-muted">
        {APP_VERSION} · {ECOBOX_ENV} · {services.deviceId}
      </p>
    </div>
  );
}
